import React, { useMemo, useState } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid,
  ReferenceArea, ReferenceLine, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  UNCERTAIN_ZONE_SD, mean, stdDev, getQuadrant, getUncertainType, RSI_CMJ_METRIC,
} from '../pages/physicalTestingShared';

const QUADRANT_COLOR = { 1: '#10b981', 2: '#8b5cf6', 3: '#ef4444', 4: '#f59e0b' };
const UNCERTAIN_COLOR = '#9ca3af';
const PLAYER_DOT_COLOR = '#3b82f6';
const BAND_FILL = '#9ca3af';
const BAND_OPACITY = 0.15;

function pad(min, max, frac = 0.15) {
  if (min === max) return [min - 1, max + 1];
  const span = max - min;
  return [min - span * frac, max + span * frac];
}

function CustomDot({ cx, cy, payload, mode, highlightPlayerId }) {
  if (cx == null || cy == null) return null;
  const isHighlighted = mode === 'player' || String(payload.player_id) === String(highlightPlayerId);
  const r = isHighlighted ? 8 : 5;
  const color = payload.uncertain ? UNCERTAIN_COLOR : (mode === 'player' ? PLAYER_DOT_COLOR : QUADRANT_COLOR[payload.quadrant]);
  return (
    <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={payload.uncertain ? 0.55 : 0.9} stroke="var(--bg-card)" strokeWidth={1.5} />
  );
}

function ScatterTooltip({ active, payload, metric }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{d.name}</div>
      <div style={{ color: 'var(--text-muted)' }}>{metric.tooltipXName}: <strong style={{ color: 'var(--text-secondary)' }}>{metric.xFormat(d.xRaw)}{metric.tooltipXUnit}</strong></div>
      <div style={{ color: 'var(--text-muted)' }}>{metric.tooltipYName}: <strong style={{ color: 'var(--text-secondary)' }}>{metric.yFormat(d.yRaw)}{metric.tooltipYUnit}</strong></div>
      <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{metric.labels[d.quadrant]}{d.uncertain ? ' · uncertain' : ''}</div>
    </div>
  );
}

/**
 * Quadrant scatter matrix, plotted against the squad mean with an "uncertain zone"
 * band derived from standard deviation. Used both for the staff squad-wide view
 * ('squad' mode) and the player's own single-dot view ('player' mode) — stats are
 * always computed from the full `data` array passed in, never from a subset, so
 * both modes agree on the same crosshairs/bands/quadrants.
 *
 * `metric` fully describes the axis pair (which fields to read, how to transform
 * them into "higher is better" plot space, and all chart/sidebar/tooltip copy) —
 * see RSI_CMJ_METRIC / SPRINT_ISRT_METRIC in physicalTestingShared.js.
 */
export default function QuadrantMatrix({ mode = 'squad', playerId = null, data, uncertainZoneSD = UNCERTAIN_ZONE_SD, metric = RSI_CMJ_METRIC }) {
  const { xKey, yKey, xTransform, yTransform, labels, descriptions, captions, emptyStateText } = metric;

  const stats = useMemo(() => {
    const xValues = data.filter(d => d[xKey] != null).map(d => xTransform(Number(d[xKey])));
    const yValues = data.filter(d => d[yKey] != null).map(d => yTransform(Number(d[yKey])));
    const meanX = mean(xValues);
    const meanY = mean(yValues);
    const sdX = stdDev(xValues);
    const sdY = stdDev(yValues);
    const bandX = sdX != null ? uncertainZoneSD * sdX : null;
    const bandY = sdY != null ? uncertainZoneSD * sdY : null;
    return { xValues, yValues, meanX, meanY, sdX, sdY, bandX, bandY };
  }, [data, uncertainZoneSD, xKey, yKey, xTransform, yTransform]);

  const points = useMemo(() => {
    const { meanX, meanY, bandX, bandY } = stats;
    if (meanX == null || meanY == null) return [];
    return data
      .filter(d => d[xKey] != null && d[yKey] != null)
      .map(d => {
        const x = xTransform(Number(d[xKey]));
        const y = yTransform(Number(d[yKey]));
        const uncertainType = getUncertainType(x, y, meanX, meanY, bandX, bandY);
        return {
          ...d, x, y,
          xRaw: Number(d[xKey]), yRaw: Number(d[yKey]),
          quadrant: getQuadrant(x, y, meanX, meanY),
          uncertain: uncertainType === 'both', // chart desaturation: both axes only
          uncertainType,                       // sidebar note: any axis
        };
      });
  }, [data, stats, xKey, yKey, xTransform, yTransform]);

  const [viewMode, setViewMode] = useState('graph');

  if (mode === 'player') {
    const myPoint = points.find(p => String(p.player_id) === String(playerId));
    if (!myPoint || stats.meanX == null || stats.meanY == null) {
      return (
        <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', fontSize: 12, padding: '24px 12px' }}>
          Not enough test data yet to show your squad profiling position.
        </div>
      );
    }
    return <QuadrantChart stats={stats} points={[myPoint]} mode="player" highlightPlayerId={playerId} caption={captions[myPoint.quadrant]} metric={metric} />;
  }

  const byQuadrant = { 1: [], 2: [], 3: [], 4: [] };
  const lastName = name => (name || '').split(' ').slice(-1)[0];
  points.forEach(p => byQuadrant[p.quadrant].push(p));
  [1, 2, 3, 4].forEach(q => byQuadrant[q].sort((a, b) => lastName(a.name).localeCompare(lastName(b.name))));

  const uncertainLabel = { both: 'Borderline on both metrics', x: 'Borderline on reactive strength', y: 'Borderline on peak power' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 4, background: 'var(--border)', borderRadius: 8, padding: 4, border: '1px solid var(--border-strong)' }}>
          {[{ id: 'graph', label: 'Graph' }, { id: 'table', label: 'Table' }].map(v => (
            <button key={v.id} onClick={() => setViewMode(v.id)}
              style={{ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: viewMode === v.id ? '#3b82f6' : 'transparent', color: viewMode === v.id ? '#fff' : 'var(--text-secondary)', fontWeight: viewMode === v.id ? 600 : 400, fontSize: 12 }}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {points.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>{emptyStateText}</div>
      ) : viewMode === 'graph' ? (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 480px', minWidth: 320 }}>
            <QuadrantChart stats={stats} points={points} mode="squad" metric={metric} />
          </div>
          <div style={{ flex: '0 0 260px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[1, 2, 3, 4].map(q => (
              <div key={q} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: QUADRANT_COLOR[q], flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{labels[q]}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>({byQuadrant[q].length})</span>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 6px' }}>{descriptions[q]}</p>
                {byQuadrant[q].length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>—</div>
                ) : byQuadrant[q].map(p => (
                  <div key={p.player_id} style={{ padding: '2px 0' }}>
                    <div style={{ fontSize: 12, color: p.uncertain ? UNCERTAIN_COLOR : 'var(--text-secondary)' }}>{p.name}</div>
                    {p.uncertainType && (
                      <div style={{ fontSize: 10, color: 'var(--text-dimmed)', fontStyle: 'italic' }}>{uncertainLabel[p.uncertainType]}</div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-strong)' }}>
                {['Player', 'Position', metric.xColumnLabel, metric.yColumnLabel, 'Profile'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...points].sort((a, b) => lastName(a.name).localeCompare(lastName(b.name))).map(p => (
                <tr key={p.player_id} style={{ borderBottom: '1px solid var(--border-strong)' }}>
                  <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 600 }}>{p.name}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13 }}>{p.position || '—'}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13 }}>{metric.xFormat(p.xRaw)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13 }}>{metric.yFormat(p.yRaw)}</td>
                  <td style={{ padding: '8px 12px', fontSize: 13 }}>
                    <span style={{ color: p.uncertain ? UNCERTAIN_COLOR : QUADRANT_COLOR[p.quadrant], fontWeight: 700 }}>
                      {labels[p.quadrant]}
                    </span>
                    {p.uncertainType && (
                      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', fontStyle: 'italic' }}>{uncertainLabel[p.uncertainType]}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function QuadrantChart({ stats, points, mode, highlightPlayerId, caption, metric }) {
  const { meanX, meanY, bandX, bandY, xValues, yValues } = stats;
  const { labels, explanations, xLabel, yLabel, xFormat, yFormat, xTransform, yTransform, legendText } = metric;
  // Domain is always derived from the full squad's spread, never just the points
  // being plotted — in player mode there's only one point, so sizing the domain
  // to fit it (and the mean) alone produced a tiny, "zoomed in" range that cut
  // the band/quadrant context off. Both modes now show the same overall shape.
  const [xMin, xMax] = pad(Math.min(...xValues), Math.max(...xValues));
  const [yMin, yMax] = pad(Math.min(...yValues), Math.max(...yValues));

  return (
    <div>
      <ResponsiveContainer width="100%" height={mode === 'player' ? 240 : 360}>
        <ScatterChart margin={{ top: 16, right: 16, bottom: 16, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis type="number" dataKey="x" domain={[xMin, xMax]} stroke="var(--text-dimmed)" tickFormatter={v => xFormat(xTransform(v))}
            tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} label={{ value: xLabel, position: 'insideBottom', offset: -8, fill: 'var(--text-muted)', fontSize: 11 }} />
          <YAxis type="number" dataKey="y" domain={[yMin, yMax]} stroke="var(--text-dimmed)" tickFormatter={v => yFormat(yTransform(v))}
            tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} label={{ value: yLabel, angle: -90, position: 'insideLeft', fill: 'var(--text-muted)', fontSize: 11 }} />
          <ZAxis range={[60, 60]} />

          {/* Uncertain-zone band: a vertical strip + horizontal strip straddling each crosshair */}
          {bandX != null && (
            <ReferenceArea x1={meanX - bandX} x2={meanX + bandX} fill={BAND_FILL} fillOpacity={BAND_OPACITY} ifOverflow="hidden" stroke="none" />
          )}
          {bandY != null && (
            <ReferenceArea y1={meanY - bandY} y2={meanY + bandY} fill={BAND_FILL} fillOpacity={BAND_OPACITY} ifOverflow="hidden" stroke="none" />
          )}

          {/* Transparent quadrant areas, used only to position the quadrant labels */}
          <ReferenceArea x1={meanX} x2={xMax} y1={meanY} y2={yMax} fill="transparent" stroke="none"
            label={{ value: labels[1], position: 'insideTopRight', fill: 'var(--text-dimmed)', fontSize: 9, fontWeight: 700 }} />
          <ReferenceArea x1={xMin} x2={meanX} y1={meanY} y2={yMax} fill="transparent" stroke="none"
            label={{ value: labels[2], position: 'insideTopLeft', fill: 'var(--text-dimmed)', fontSize: 9, fontWeight: 700 }} />
          <ReferenceArea x1={xMin} x2={meanX} y1={yMin} y2={meanY} fill="transparent" stroke="none"
            label={{ value: labels[3], position: 'insideBottomLeft', fill: 'var(--text-dimmed)', fontSize: 9, fontWeight: 700 }} />
          <ReferenceArea x1={meanX} x2={xMax} y1={yMin} y2={meanY} fill="transparent" stroke="none"
            label={{ value: labels[4], position: 'insideBottomRight', fill: 'var(--text-dimmed)', fontSize: 9, fontWeight: 700 }} />

          <ReferenceLine x={meanX} stroke="var(--text-dimmed)" strokeDasharray="4 4" />
          <ReferenceLine y={meanY} stroke="var(--text-dimmed)" strokeDasharray="4 4" />

          {mode === 'squad' && <Tooltip content={<ScatterTooltip metric={metric} />} cursor={{ strokeDasharray: '3 3' }} />}
          <Scatter data={points} shape={props => <CustomDot {...props} mode={mode} highlightPlayerId={highlightPlayerId} />} />
        </ScatterChart>
      </ResponsiveContainer>
      {caption && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 8, padding: '0 4px' }}>{caption}</div>
      )}
      <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--bg-app)', borderRadius: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 8 }}>
          {legendText}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 6, marginBottom: 8 }}>
          {[1, 2, 3, 4].map(q => (
            <div key={q} style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>{labels[q]}:</span> {explanations[q]}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
          Quadrant position is relative to the current squad average — not an absolute performance benchmark. Always interpret alongside position, training phase, and injury history.
        </div>
      </div>
    </div>
  );
}
